import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from '@/lib/utils';
import { Thought } from '@/services/supabase/thoughts';
import { format } from 'date-fns';
import { Calendar, Edit, Heart } from 'lucide-react';
import React from 'react';

interface ThoughtDetailDialogProps {
  /**
   * Whether the dialog is open
   */
  isOpen: boolean;

  /**
   * Function to call when the dialog open state changes
   */
  onOpenChange: (open: boolean) => void;

  /**
   * The thought to display
   */
  thought: Thought;

  /**
   * Function to call when the edit button is clicked
   */
  onEdit: (thought: Thought) => void;

  /**
   * Function to call when the favorite button is clicked
   */
  onToggleFavorite: (thought: Thought) => void;
}

/**
 * Dialog component for displaying the full content of a thought
 */
export const ThoughtDetailDialog: React.FC<ThoughtDetailDialogProps> = ({
  isOpen,
  onOpenChange,
  thought,
  onEdit,
  onToggleFavorite,
}) => {
  // Format the date for detailed display
  const formattedDetailedDate = React.useMemo(() => {
    try {
      const date = new Date(thought.created_at);
      return format(date, 'MMMM d, yyyy');
    } catch (e) {
      return '';
    }
  }, [thought.created_at]);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden">
        {/* Header with thought title */}
        <DialogHeader className="px-6 pt-6 pb-2 border-b">
          <DialogTitle className="text-xl font-semibold">
            {thought.title || 'Untitled'}
          </DialogTitle>
        </DialogHeader>

        <div className="px-6 py-5">
          {/* Content */}
          <div className="max-w-none">
            <p className="whitespace-pre-wrap leading-relaxed text-foreground text-base">
              {thought.content || 'No content'}
            </p>
          </div>

          {/* Metadata */}
          <div className="flex items-center justify-between mt-8 pt-4 border-t border-border text-xs text-muted-foreground">
            <div className="flex items-center">
              <Calendar className="h-3.5 w-3.5 mr-1.5" />
              {formattedDetailedDate}
            </div>

            {thought.is_favorite && (
              <div className="flex items-center">
                <Heart className="h-3.5 w-3.5 mr-1.5 fill-primary text-primary" />
                <span>Favorite</span>
              </div>
            )}
          </div>
        </div>

        {/* Action buttons */}
        <div className="px-6 py-4 border-t bg-muted flex justify-end space-x-2">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              onEdit(thought);
            }}
          >
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button
            variant={thought.is_favorite ? "secondary" : "default"} // Changed outline to secondary for better visibility
            onClick={() => onToggleFavorite(thought)}
            className={cn(thought.is_favorite && "bg-secondary/80 hover:bg-secondary")}
          >
            <Heart className={cn("h-4 w-4 mr-2", thought.is_favorite ? "fill-primary text-primary" : "")} />
            {thought.is_favorite ? 'Remove from Favorites' : 'Add to Favorites'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
